import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Workflow, Filter, RefreshCw, Zap, ExternalLink } from "lucide-react";
import { ProgressBar } from "@/components/ui/progress-bar";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/EmptyState";
import { Select } from "@/components/ui/select";
import { formatDate } from "@/lib/utils";
import { useListJobs, useProcesarPendientes, type JobsFilters } from "./api";
import type { JobStatus, MikrotikJobSummary } from "./schemas";
import { JobDetailDrawer } from "./JobDetailDrawer";
// rc140 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

const STATUS_VARIANT: Record<
  JobStatus,
  "success" | "warning" | "destructive" | "secondary" | "default"
> = {
  completed: "success",
  running: "default",
  queued: "secondary",
  error: "destructive",
};

const ACTION_LABEL: Record<string, string> = {
  reconcile_contract: "Reconciliar contrato",
  reconcile_full_router: "Reconciliar todo (router)",
  delete_contract: "Borrar contrato del router",
  reconcile_router_firewall: "Recrear reglas firewall",
};

export function JobsListPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [actionFilter, setActionFilter] = useState<string>("");
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const filters: JobsFilters = useMemo(
    () => ({
      page, size: 50,
      status: statusFilter || undefined,
      action: actionFilter || undefined,
    }),
    [page, statusFilter, actionFilter],
  );

  const { data, isLoading, isFetching, refetch } = useListJobs(filters);
  const procesarMut = useProcesarPendientes();
  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  async function handleProcesarPendientes() {
    try {
      const res = await procesarMut.mutateAsync();
      if (res.reenqueued > 0 || res.recovered_stuck > 0) {
        toast.success("Pendientes procesados", {
          description: `${res.reenqueued} re-inyectados · ${res.recovered_stuck} recuperados de stuck`,
        });
      } else {
        toast.info("Nada que procesar", {
          description: "No había jobs huérfanos ni stuck.",
        });
      }
    } catch (e) {
      toast.error("Error al procesar pendientes", {
        description: (e as { message?: string })?.message ?? "Ver logs del API",
      });
    }
  }

  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Jobs MikroTik</h2>
          <p className="text-xs text-muted-foreground">
            {data ? `${data.total} jobs en total · actualización cada 5s` : "Cargando…"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm" variant="outline"
            disabled={procesarMut.isPending}
            onClick={handleProcesarPendientes}
            title="Fuerza re-inyección al stream de jobs queued huérfanos + recupera stuck. El worker hace esto automático cada 30s."
          >
            <Zap size={12} />
            {procesarMut.isPending ? "Procesando…" : "Procesar pendientes"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            <RefreshCw size={12} className={isFetching ? "animate-spin" : ""} /> Refrescar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Estado</label>
          <Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="w-36"
          >
            <option value="">Todos</option>
            <option value="queued">Encolado</option>
            <option value="running">Ejecutando</option>
            <option value="completed">Completado</option>
            <option value="error">Error</option>
          </Select>
        </div>
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Acción</label>
          <Select
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
            className="w-52"
          >
            <option value="">Todas</option>
            <option value="reconcile_contract">Reconciliar contrato</option>
            <option value="reconcile_full_router">Reconciliar todo (router)</option>
            <option value="delete_contract">Borrar contrato del router</option>
            <option value="reconcile_router_firewall">Recrear reglas firewall</option>
          </Select>
        </div>
        {(statusFilter || actionFilter) && (
          <Button
            size="sm" variant="ghost"
            onClick={() => { setStatusFilter(""); setActionFilter(""); setPage(1); }}
          >
            <Filter size={12} /> Limpiar
          </Button>
        )}
      </div>

      {isMobile ? (
        isLoading && items.length === 0 ? (
          <MobileEmpty>Cargando…</MobileEmpty>
        ) : items.length === 0 ? (
          <MobileEmpty>{statusFilter || actionFilter ? "Sin coincidencias para los filtros." : "No hay operaciones encoladas ni históricas aún."}</MobileEmpty>
        ) : (
          <MobileList>
            {items.map((j: MikrotikJobSummary) => (
              <MobileCard
                key={j.id}
                onClick={() => setSelectedJobId(j.id)}
                title={ACTION_LABEL[j.action] ?? j.action}
                subtitle={
                  j.related_entity === "contrato" && j.related_id
                    ? (j.related_label ?? `${j.related_id.slice(0, 8)}…`)
                    : j.action === "reconcile_full_router" ? "Todo el router"
                    : j.action === "reconcile_router_firewall" ? "Firewall" : "—"
                }
                right={<Badge variant={STATUS_VARIANT[j.status]}>{j.status}</Badge>}
              >
                {j.status === "running" && j.progress && j.progress.total > 0 && (
                  <div className="col-span-2 flex items-center gap-2">
                    <div className="flex-1"><ProgressBar value={j.progress.current} max={j.progress.total} size="xs" pulse /></div>
                    <span className="font-mono text-[9px] text-muted-foreground">{j.progress.current}/{j.progress.total}</span>
                  </div>
                )}
                <MobileMeta label="Router">{j.router_nombre ?? <span className="font-mono">{j.router_id.slice(0, 8)}…</span>}</MobileMeta>
                <MobileMeta label="Intento" mono>{j.attempt}</MobileMeta>
                <MobileMeta label="Encolado">{formatDate(j.enqueued_at)}</MobileMeta>
                <MobileMeta label="Completado">{j.completed_at ? formatDate(j.completed_at) : "—"}</MobileMeta>
                {j.error && <MobileMeta label="Error" span><span className="text-destructive">{j.error}</span></MobileMeta>}
              </MobileCard>
            ))}
          </MobileList>
        )
      ) : (
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Acción</TableHead>
              <TableHead>Contrato / Entidad</TableHead>
              <TableHead>Router</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Intento</TableHead>
              <TableHead>Encolado</TableHead>
              <TableHead>Completado</TableHead>
              <TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={Workflow}
                  title="Sin jobs"
                  description={statusFilter || actionFilter
                    ? "Sin coincidencias para los filtros."
                    : "No hay operaciones encoladas ni históricas aún."}
                />
              </TableEmpty>
            ) : (
              items.map((j: MikrotikJobSummary) => (
                <TableRow
                  key={j.id}
                  className="cursor-pointer"
                  onClick={() => setSelectedJobId(j.id)}
                >
                  <TableCell>{ACTION_LABEL[j.action] ?? j.action}</TableCell>
                  <TableCell className="text-xs">
                    {j.related_entity === "contrato" && j.related_id ? (
                      <Link
                        to={`/contratos/${j.related_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 hover:underline"
                        title={j.related_label ?? j.related_id}
                      >
                        <span className="truncate max-w-[260px]">
                          {j.related_label ?? `${j.related_id.slice(0, 8)}…`}
                        </span>
                        <ExternalLink size={10} className="opacity-50 shrink-0" />
                      </Link>
                    ) : j.action === "reconcile_full_router" ? (
                      <span className="italic text-muted-foreground">Todo el router</span>
                    ) : j.action === "reconcile_router_firewall" ? (
                      <span className="italic text-muted-foreground">Firewall</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {j.router_nombre ?? (
                      <span className="font-mono">{j.router_id.slice(0, 8)}…</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 min-w-[110px]">
                      <Badge variant={STATUS_VARIANT[j.status]}>{j.status}</Badge>
                      {j.status === "running" && j.progress && j.progress.total > 0 && (
                        <div className="flex flex-col gap-0.5">
                          <ProgressBar
                            value={j.progress.current}
                            max={j.progress.total}
                            size="xs"
                            pulse
                          />
                          <span className="text-[9px] font-mono text-muted-foreground">
                            {j.progress.current}/{j.progress.total}
                          </span>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono">{j.attempt}</TableCell>
                  <TableCell>{formatDate(j.enqueued_at)}</TableCell>
                  <TableCell>
                    {j.completed_at ? formatDate(j.completed_at) : "—"}
                  </TableCell>
                  <TableCell className="max-w-[240px] truncate text-xs text-destructive">
                    {j.error ?? ""}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
            <span className="text-muted-foreground">
              Página {data?.page ?? 1} de {totalPages}
            </span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Button size="sm" variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </div>
      )}

      <JobDetailDrawer
        jobId={selectedJobId}
        open={selectedJobId !== null}
        onOpenChange={(v) => { if (!v) setSelectedJobId(null); }}
      />
    </div>
  );
}
